import React, { useState, useEffect } from 'react';
import { Incident, User, UserRole, IncidentStatus, Severity, IncidentType, Entity, Organization } from '../types';
import { resolveIncident, disputeIncident, updateIncident, getOrganizations, getAllEntities, deleteIncident } from '../services/apiService';
import { LoadingSpinner } from './icons';

interface IncidentDetailModalProps {
  incident: Incident;
  user: User;
  onClose: () => void;
  onUpdate: (incident: Incident) => void;
  onDelete: (incidentId: string) => void;
}

const DetailItem: React.FC<{ label: string; value?: string | number }> = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-4 py-2">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900 col-span-2">{value || 'N/A'}</dd>
  </div>
);

const IncidentDetailModal: React.FC<IncidentDetailModalProps> = ({ incident, user, onClose, onUpdate, onDelete }) => {
  const [isResolving, setIsResolving] = useState(false);
  const [isDisputing, setIsDisputing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showResolveForm, setShowResolveForm] = useState(false);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [disputeReason, setDisputeReason] = useState('');

  const [editableIncident, setEditableIncident] = useState<Partial<Incident>>({});

  // State for super admin editing
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [entitySearch, setEntitySearch] = useState('');
  const [filteredEntities, setFilteredEntities] = useState<Entity[]>([]);

  const canManageAll = user.permissions.includes('manage_all_incidents');
  const isOwner = user.organizationId === incident.reporterOrgId;
  const canManageOwn = user.permissions.includes('manage_incidents_own_org');
  const canPerformActions = (canManageAll || (isOwner && canManageOwn)) && incident.status !== IncidentStatus.RESOLVED;
  const isSuperAdmin = user.role === UserRole.SUPER_ADMIN;

  // Helper to format a date string into YYYY-MM-DDTHH:mm for datetime-local input
  const toLocalISOString = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const pad = (num: number) => num.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  useEffect(() => {
    setEditableIncident({
        description: incident.description,
        severity: incident.severity,
        incidentType: incident.incidentType,
        debtAmount: incident.debtAmount,
        status: incident.status,
        incidentDate: toLocalISOString(incident.incidentDate),
        reportedDate: toLocalISOString(incident.reportedDate),
        reporterOrgId: incident.reporterOrgId,
        entityId: incident.entityId,
    });
     if (canManageAll) {
        const fetchAdminData = async () => {
            try {
                const [orgs, entities] = await Promise.all([getOrganizations(), getAllEntities()]);
                setOrganizations(orgs);
                setAllEntities(entities);
            } catch (e) {
                console.error("Failed to fetch data for admin edit", e);
            }
        };
        fetchAdminData();
    }
  }, [incident, canManageAll]);
  
  const handleFieldChange = (field: keyof Incident, value: any) => {
    setEditableIncident(prev => ({ ...prev, [field]: value }));
  };

  const handleResolve = async () => {
    if (!resolutionNotes.trim()) return;
    setIsResolving(true);
    try {
      const updatedIncident = await resolveIncident(incident.id, resolutionNotes, user);
      onUpdate(updatedIncident);
    } catch(e) {
      console.error("Failed to resolve incident", e);
    } finally {
      setIsResolving(false);
      setShowResolveForm(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) return;
    setIsDisputing(true);
    try {
      const updatedIncident = await disputeIncident(incident.id, disputeReason, user);
      onUpdate(updatedIncident);
    } catch(e) {
      console.error("Failed to dispute incident", e);
    } finally {
      setIsDisputing(false);
      setShowDisputeForm(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedIncident = await updateIncident(incident.id, editableIncident, user);
      onUpdate(updatedIncident);
      setIsEditing(false);
    } catch (e) {
      console.error("Failed to save incident", e);
    } finally {
        setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to permanently delete this incident? This action cannot be undone.")) {
        setIsDeleting(true);
        try {
            await deleteIncident(incident.id, user);
            onDelete(incident.id);
            onClose();
        } catch (err) {
            alert(`Failed to delete incident: ${(err as Error).message}`);
        } finally {
            setIsDeleting(false);
        }
    }
  };

  const handleEntitySearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setEntitySearch(term);
    if (term.length > 1) {
        const filtered = allEntities.filter(entity => 
            entity.name.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredEntities(filtered);
    } else {
        setFilteredEntities([]);
    }
  };

  const handleSelectEntity = (entity: Entity) => {
    handleFieldChange('entityId', entity.id);
    setEntitySearch(entity.name);
    setFilteredEntities([]);
  };
  
  const selectedEntityName = allEntities.find(e => e.id === editableIncident.entityId)?.name || incident.entityDetails.name;


  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative mx-auto border w-full max-w-3xl shadow-lg rounded-md bg-white">
        <div className="p-5 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-start">
            <div>
                <h3 className="text-2xl leading-6 font-bold text-gray-900">Incident Details</h3>
                <p className="mt-1 text-sm text-gray-500 font-mono break-all">{incident.id}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl font-bold -mt-2">&times;</button>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {/* Left Column */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-semibold mb-2">Incident Information</h4>
              <dl>
                {isEditing && canManageAll ? (
                    <div className="grid grid-cols-3 gap-4 py-2">
                        <label htmlFor="status" className="text-sm font-medium text-gray-500">Status</label>
                        <select id="status" value={editableIncident.status} onChange={e => handleFieldChange('status', e.target.value)} className="col-span-2 text-sm p-1 border rounded-md">
                            {Object.values(IncidentStatus).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                ) : <DetailItem label="Status" value={incident.status} />}
                {isEditing ? (
                    <div className="grid grid-cols-3 gap-4 py-2">
                        <label htmlFor="incidentType" className="text-sm font-medium text-gray-500">Type</label>
                        <select id="incidentType" value={editableIncident.incidentType} onChange={e => handleFieldChange('incidentType', e.target.value)} className="col-span-2 text-sm p-1 border rounded-md">
                            {Object.values(IncidentType).map(it => <option key={it} value={it}>{it}</option>)}
                        </select>
                    </div>
                ) : <DetailItem label="Type" value={incident.incidentType} />}
                {isEditing ? (
                     <div className="grid grid-cols-3 gap-4 py-2">
                        <label htmlFor="severity" className="text-sm font-medium text-gray-500">Severity</label>
                        <select id="severity" value={editableIncident.severity} onChange={e => handleFieldChange('severity', e.target.value)} className="col-span-2 text-sm p-1 border rounded-md">
                            {Object.values(Severity).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                ) : <DetailItem label="Severity" value={incident.severity} />}
                {isEditing && canManageAll ? (
                     <div className="grid grid-cols-3 gap-4 py-2">
                        <label htmlFor="incidentDate" className="text-sm font-medium text-gray-500">Incident Date</label>
                        <input id="incidentDate" type="datetime-local" value={editableIncident.incidentDate} onChange={e => handleFieldChange('incidentDate', e.target.value)} className="col-span-2 text-sm p-1 border rounded-md" />
                    </div>
                ) : <DetailItem label="Incident Date" value={new Date(incident.incidentDate).toLocaleString()} />}
                
                {isEditing && canManageAll ? (
                     <div className="grid grid-cols-3 gap-4 py-2">
                        <label htmlFor="reportedDate" className="text-sm font-medium text-gray-500">Reported Date</label>
                        <input id="reportedDate" type="datetime-local" value={editableIncident.reportedDate as string} onChange={e => handleFieldChange('reportedDate', e.target.value)} className="col-span-2 text-sm p-1 border rounded-md" />
                    </div>
                ) : <DetailItem label="Reported Date" value={new Date(incident.reportedDate).toLocaleString()} />}

                {isEditing && canManageAll ? (
                    <div className="grid grid-cols-3 gap-4 py-2">
                        <label htmlFor="reporterOrgId" className="text-sm font-medium text-gray-500">Reported By</label>
                        <select id="reporterOrgId" value={editableIncident.reporterOrgId} onChange={e => handleFieldChange('reporterOrgId', e.target.value)} className="col-span-2 text-sm p-1 border rounded-md">
                            <option value={user.organizationId}>{user.organizationName}</option>
                            {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                        </select>
                    </div>
                ) : <DetailItem label="Reported By" value={incident.reporterOrgName} />}
                
                {isEditing && editableIncident.incidentType === IncidentType.UNPAID_FEES_DEBT ? (
                     <div className="grid grid-cols-3 gap-4 py-2">
                        <label htmlFor="debtAmount" className="text-sm font-medium text-gray-500">Debt Amount</label>
                        <input id="debtAmount" type="number" value={editableIncident.debtAmount || ''} onChange={e => handleFieldChange('debtAmount', Number(e.target.value))} className="col-span-2 text-sm p-1 border rounded-md" />
                    </div>
                ) : incident.debtAmount ? <DetailItem label="Debt Amount" value={`$${incident.debtAmount.toLocaleString()}`} /> : null}
              </dl>
            </div>
            
            {/* Right Column */}
            <div className="border-t border-gray-200 pt-4">
                <h4 className="font-semibold mb-2">Entity Details ({incident.entityType})</h4>
                {isEditing && canManageAll ? (
                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-700">Reassign Entity</label>
                        <input 
                            type="text" 
                            value={entitySearch}
                            onChange={handleEntitySearchChange}
                            placeholder="Search to reassign..."
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                        />
                        {filteredEntities.length > 0 && (
                             <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg">
                                {filteredEntities.map(entity => (
                                    <li key={entity.id} onClick={() => handleSelectEntity(entity)} className="px-4 py-2 hover:bg-primary-light hover:text-white cursor-pointer">
                                        {entity.name}
                                    </li>
                                ))}
                             </ul>
                        )}
                        <p className="text-xs text-gray-600 mt-2">Selected: <span className="font-semibold">{selectedEntityName}</span></p>
                    </div>
                ) : (
                    <dl>
                        <DetailItem label="Name" value={incident.entityDetails.name} />
                        { 'licenceNumber' in incident.entityDetails && <DetailItem label="Licence #" value={incident.entityDetails.licenceNumber} /> }
                        { 'abn' in incident.entityDetails && <DetailItem label="ABN" value={incident.entityDetails.abn} /> }
                        { 'plateNumber' in incident.entityDetails && <DetailItem label="Plate #" value={incident.entityDetails.plateNumber} /> }
                    </dl>
                )}
            </div>
          </div>
          
          <div className="mt-4 border-t border-gray-200 pt-4">
            <h4 className="font-semibold">Description</h4>
             {isEditing ? (
                <textarea 
                    value={editableIncident.description} 
                    onChange={e => handleFieldChange('description', e.target.value)}
                    rows={4}
                    className="w-full text-sm text-gray-700 mt-2 p-2 bg-white border rounded-md"
                />
             ) : (
                <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-3 rounded-md">{incident.description}</p>
             )}
          </div>

          {incident.dispute && (
            <div className="mt-4 border-t border-gray-200 pt-4">
                <h4 className="font-semibold text-orange-700">Dispute Information</h4>
                <p className="text-sm text-gray-700 mt-2 bg-orange-50 p-3 rounded-md">
                    <strong>Reason:</strong> {incident.dispute.reason}<br />
                    <strong>Date:</strong> {new Date(incident.dispute.disputedDate).toLocaleString()}
                </p>
            </div>
          )}

          {incident.resolution && (
            <div className="mt-4 border-t border-gray-200 pt-4">
                <h4 className="font-semibold text-green-700">Resolution</h4>
                <p className="text-sm text-gray-700 mt-2 bg-green-50 p-3 rounded-md">
                    <strong>Notes:</strong> {incident.resolution.notes}<br />
                    <strong>Date:</strong> {new Date(incident.resolution.resolvedDate).toLocaleString()}
                </p>
            </div>
          )}

          {/* Action Forms */}
          {canPerformActions && showResolveForm && !isEditing && (
            <div className="mt-4 border-t pt-4">
              <h4 className="font-semibold">Resolve Incident</h4>
              <textarea 
                className="w-full mt-2 p-2 border rounded-md" 
                rows={3} 
                placeholder="Enter resolution notes..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
              />
              <div className="flex gap-2 mt-2">
                <button onClick={handleResolve} disabled={isResolving} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400">
                    {isResolving ? <LoadingSpinner className="w-5 h-5"/> : 'Confirm Resolution'}
                </button>
                <button onClick={() => setShowResolveForm(false)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          )}

           {canPerformActions && showDisputeForm && !isEditing && (
            <div className="mt-4 border-t pt-4">
              <h4 className="font-semibold">Dispute Incident</h4>
              <textarea 
                className="w-full mt-2 p-2 border rounded-md" 
                rows={3} 
                placeholder="Enter reason for dispute..."
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
              />
              <div className="flex gap-2 mt-2">
                <button onClick={handleDispute} disabled={isDisputing} className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 disabled:bg-gray-400">
                    {isDisputing ? <LoadingSpinner className="w-5 h-5"/> : 'Submit Dispute'}
                </button>
                <button onClick={() => setShowDisputeForm(false)} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          )}
        </div>

        <div className="items-center px-4 py-3 border-t bg-gray-50 flex flex-wrap justify-between gap-3 rounded-b-md">
            <div>
                 {isSuperAdmin && !isEditing && (
                    <button onClick={handleDelete} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-red-700 disabled:bg-gray-400">
                        {isDeleting ? <LoadingSpinner className="w-5 h-5"/> : 'Delete'}
                    </button>
                 )}
            </div>
            <div className="flex flex-wrap justify-end gap-3">
                {isEditing ? (
                    <>
                    <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 bg-primary text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-primary-light disabled:bg-gray-400">
                            {isSaving ? <LoadingSpinner className="w-5 h-5"/> : 'Save Changes'}
                    </button>
                    <button onClick={() => { setIsEditing(false); setEntitySearch(''); setFilteredEntities([]); }} className="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-auto shadow-sm hover:bg-gray-300">
                        Cancel
                    </button>
                    </>
                ) : (
                    <>
                        {canPerformActions && (
                            <button onClick={() => { setIsEditing(true); setEntitySearch(incident.entityDetails.name); }} className="px-4 py-2 bg-gray-600 text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-gray-700">
                                Edit
                            </button>
                        )}
                        {canPerformActions && !showDisputeForm && (
                            <button 
                            onClick={() => { setShowDisputeForm(true); setShowResolveForm(false); }} 
                            className="px-4 py-2 bg-orange-500 text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-300"
                            >
                            Dispute
                            </button>
                        )}
                        {canPerformActions && !showResolveForm && (
                            <button 
                            onClick={() => { setShowResolveForm(true); setShowDisputeForm(false); }} 
                            className="px-4 py-2 bg-primary text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-primary-dark"
                            >
                            Resolve
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-auto shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                            Close
                        </button>
                    </>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentDetailModal;
